import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Mail, ShieldCheck } from 'lucide-react';
import { Login2faChallenge } from '../types';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

const OTP_LENGTH = 6;
const emptyDigits = () => Array(OTP_LENGTH).fill('') as string[];

interface Business2faOtpFormProps {
  challenge: Login2faChallenge;
  onChallengeChange: (challenge: Login2faChallenge) => void;
  onVerified: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
}

/**
 * Shared 6-digit business 2FA form (login step-up and dashboard gate).
 */
const Business2faOtpForm: React.FC<Business2faOtpFormProps> = ({
  challenge,
  onChallengeChange,
  onVerified,
  onCancel,
  cancelLabel = 'Back',
}) => {
  const { verifyLogin2fa, resendLogin2fa } = useAuth();
  const [digits, setDigits] = useState<string[]>(emptyDigits);
  const [rememberBrowser, setRememberBrowser] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const autoSubmitRef = useRef(false);
  const rememberBrowserRef = useRef(false);
  const code = digits.join('');

  useEffect(() => {
    rememberBrowserRef.current = rememberBrowser;
  }, [rememberBrowser]);

  useEffect(() => {
    autoSubmitRef.current = false;
    setDigits(emptyDigits());
    setRememberBrowser(false);
    rememberBrowserRef.current = false;
    setErrorMessage(null);
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
      inputRefs.current[0]?.select();
    }, 50);
    return () => clearTimeout(timer);
  }, [challenge.challengeId]);

  const focusIndex = (index: number) => {
    const clamped = Math.max(0, Math.min(OTP_LENGTH - 1, index));
    requestAnimationFrame(() => {
      const el = inputRefs.current[clamped];
      el?.focus();
      el?.select();
    });
  };

  const submitCode = async (nextCode: string) => {
    if (!/^\d{6}$/.test(nextCode)) {
      setErrorMessage('Enter the 6-digit verification code sent to your email');
      return;
    }
    if (autoSubmitRef.current || isLoading) return;

    autoSubmitRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await verifyLogin2fa(challenge.challengeId, nextCode, rememberBrowserRef.current);
      toast.success('Verification successful');
      onVerified();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        (!error.response && (error.request || error.message?.toLowerCase().includes('network'))
          ? 'Unable to connect to the server. Please check your internet connection and try again.'
          : 'Verification failed. Please try again.');
      setErrorMessage(errorMsg);
      setDigits(emptyDigits());
      autoSubmitRef.current = false;
      if (
        errorMsg.toLowerCase().includes('sign in again') ||
        errorMsg.toLowerCase().includes('expired')
      ) {
        onCancel?.();
      } else {
        focusIndex(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const applyDigitsFromIndex = (startIndex: number, incomingDigits: string) => {
    const cleaned = incomingDigits.replace(/\D/g, '');
    if (!cleaned) return;

    setDigits((prev) => {
      const next = [...prev];
      const chars = cleaned.slice(0, OTP_LENGTH - startIndex).split('');
      chars.forEach((digit, offset) => {
        next[startIndex + offset] = digit;
      });
      const nextFocus = Math.min(startIndex + chars.length, OTP_LENGTH - 1);
      focusIndex(nextFocus);
      return next;
    });
    if (errorMessage) setErrorMessage(null);
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyDigitsFromIndex(index, value);
      return;
    }
    const digit = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      if (digit && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
      }
      return next;
    });
    if (errorMessage) setErrorMessage(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      if (index > 0) focusIndex(index - 1);
      if (errorMessage) setErrorMessage(null);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusIndex(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault();
      focusIndex(index + 1);
      return;
    }
    if (/^\d$/.test(e.key) && digits[index]) {
      e.preventDefault();
      handleDigitChange(index, e.key);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    applyDigitsFromIndex(index, e.clipboardData.getData('text') || '');
  };

  const handleResend = async () => {
    if (isResending || isLoading) return;
    setIsResending(true);
    setErrorMessage(null);
    try {
      const nextChallenge = await resendLogin2fa(challenge.challengeId);
      onChallengeChange(nextChallenge);
      toast.success('A new verification code has been sent');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to resend verification code';
      setErrorMessage(errorMsg);
      if (errorMsg.toLowerCase().includes('sign in again') || errorMsg.toLowerCase().includes('expired')) {
        onCancel?.();
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        void submitCode(code);
      }}
      noValidate
    >
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3 items-start">
          <Mail className="h-5 w-5 text-blue-700 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-900 leading-relaxed">
            Because online payments are enabled for your business, we sent a verification code to{' '}
            <strong>{challenge.email}</strong>. The code expires in 10 minutes.
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="business2fa-0" className="block text-sm font-medium text-gray-700 mb-2">
          Verification code
        </label>
        <div
          className="flex items-center justify-between gap-2 sm:gap-3"
          role="group"
          aria-label="6-digit verification code"
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              id={`business2fa-${index}`}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              disabled={isLoading}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              onFocus={(e) => e.target.select()}
              className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold rounded-lg border bg-white text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-60 disabled:cursor-not-allowed ${
                errorMessage
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:border-blue-900 focus:ring-blue-200'
              }`}
            />
          ))}
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </div>
      )}

      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={rememberBrowser}
          disabled={isLoading}
          onChange={(e) => setRememberBrowser(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
        />
        <span className="text-sm text-gray-700 leading-relaxed">
          Remember this browser for 14 days
          <span className="block text-xs text-gray-500 mt-0.5">
            Skip email verification on this browser until then. Other browsers still require a code.
          </span>
        </span>
      </label>

      <button
        type="submit"
        disabled={isLoading || code.length !== 6}
        className="aacore-btn-primary w-full justify-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
      >
        {isLoading ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Verifying...
          </div>
        ) : (
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Verify and continue
          </span>
        )}
      </button>

      <div className="flex items-center justify-between text-sm">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-gray-900 bg-transparent border-0 p-0 cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            {cancelLabel}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleResend}
          disabled={isResending || isLoading}
          className="font-medium text-blue-900 hover:text-blue-800 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
        >
          {isResending ? 'Sending...' : 'Resend code'}
        </button>
      </div>
    </form>
  );
};

export default Business2faOtpForm;
